import { useCallback, useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';

import { Toast, Button, ActionsMenu, SearchInput } from '@pletly/ui';
import { getName } from '@pletly/shared';
import { ChangePasswordModal, useUser } from '@pletly/feature-auth';

import { useUpdateCaregiverMemberMutation } from '../__generated__/updateCaregiverMember.mutation.generated';
import { OrganisationRoleCell } from './OrganisationRoleCell';
import { OrganisationRole } from '../../../../__generated__/types';
import { useArchiveMember } from '../useArchiveMember';
import { withTranslation } from '../../../../locales/hoc';
import { useResetMemberPassword } from '../useResetMemberPassword';
import { OrganisationMemberDataType, OrganisationMembersTable } from '../OrganisationMembersTable';
import { EmailCell } from '../EmailCell';
import { PressableCell } from '../../../components/PressableCell';
import { useGoToProfile } from '../../../components/useGoToProfile';
import { UsersContainer } from '../UsersContainer';
import { Spacer } from '../Spacer';
import { useMemberSearch } from '../useMemberSearch';

type Props = {
	organisationUuid: string;
} & { data: OrganisationMemberDataType[] };

export const CaregiversTable = withTranslation(({ data, organisationUuid }: Props) => {
	const { t } = useTranslation();
	const { navigate } = useNavigation();
	const user = useUser();
	const goToProfile = useGoToProfile();
	const [updateCaregiverMember] = useUpdateCaregiverMemberMutation();
	const { handleArchive } = useArchiveMember();
	const { changePasswordModal, handleResetPress, handleChangePassword } = useResetMemberPassword();
	const { searchInput, filteredData } = useMemberSearch({ data });

	const handleRoleChange = useCallback(
		async (memberId: string, role: OrganisationRole) => {
			try {
				await updateCaregiverMember({
					variables: {
						data: {
							id: memberId,
							role,
						},
					},
				});
				Toast.show({
					type: 'success',
					text1: t('toasts.success.role-updated'),
				});
			} catch {
				Toast.show({
					type: 'error',
					text1: t('toasts.error.something-wrong'),
				});
			}
		},
		[updateCaregiverMember, t],
	);

	const goToAssignToTeams = useCallback(
		(memberId: string) => navigate('AssignMemberToTeams', { memberId, organisationUuid }),
		[navigate, organisationUuid],
	);

	const COLUMNS_CONFIG = useMemo(
		() => [
			{
				name: t('table.header.name'),
				accessor: 'firstName' as const,
				renderCell: ({ row }: { row: Props['data'][0] }) => (
					<PressableCell text={getName(row)} onPress={() => goToProfile(row)} />
				),
			},
			{
				name: t('table.header.username'),
				accessor: 'username' as const,
			},
			{
				name: t('table.header.role'),
				accessor: 'organisationRole' as const,
				renderCell: ({ row }: { row: Props['data'][0] }) => (
					<OrganisationRoleCell row={row} onRoleChange={handleRoleChange} />
				),
			},
			{
				name: t('table.header.email'),
				accessor: 'email' as const,
				renderCell: ({ row }: { row: Props['data'][0] }) => <EmailCell email={row.email} />,
			},
		],
		[handleRoleChange, t, goToProfile],
	);

	const renderRowActions = useCallback(
		({ row }: { row: OrganisationMemberDataType }) => (
			<ActionsMenu
				actions={[
					{
						title: t('table.actions.view-profile'),
						onPress: () => goToProfile(row),
					},
					{
						title: t('table.actions.assign-to-teams'),
						onPress: () => goToAssignToTeams(row.memberId),
					},
					{
						title: t('table.actions.reset-password'),
						onPress: () => handleResetPress({ memberId: row.memberId, hasEmail: !!row.email }),
					},
					{
						title: t('table.actions.archive'),
						onPress: () => handleArchive(row),
						hidden: row.id === user?.id,
					},
				]}
			/>
		),
		[goToAssignToTeams, goToProfile, handleArchive, handleResetPress, t, user?.id],
	);

	return (
		<UsersContainer>
			<UsersContainer.Header>
				<SearchInput {...searchInput} />
				<Spacer />
				<Button
					style={styles.newMemberButton}
					variant="filled"
					color="primary"
					onPress={() => navigate('NewOrganisationMember', { organisationUuid, type: 'caregiver' })}
				>
					{t('new-employee')}
				</Button>
			</UsersContainer.Header>

			<OrganisationMembersTable
				data={filteredData}
				columnsConfig={COLUMNS_CONFIG}
				renderRowActions={renderRowActions}
			/>
			<ChangePasswordModal
				skipOldPassword
				visible={changePasswordModal.visible}
				onClose={changePasswordModal.close}
				onChangePassword={handleChangePassword}
			/>
		</UsersContainer>
	);
});

const styles = StyleSheet.create({
	newMemberButton: {
		alignSelf: 'flex-end',
	},
});
